<script setup>import { computed as _computed } from 'vue';

  import DropdownWidget from "./dropdown-widget.vue";
  import { directive as vTippy } from "vue-tippy";

  import {
    copyIngredientPath,
    editIngredientPath,
    ingredientPath,
    showRecipesForIngredientPath,
  } from "routes";

  const props = defineProps({
    ingredientId: { type: Number, required: true },
    userId: { type: [Number, String], required: true },
    archived: { type: Boolean, default: false },
    canArchive: { type: Boolean, default: true },
    archiveDisabledReason: { type: String, default: null },
    ingredientName: { type: String, default: "" },
  });

  function openArchiveModal() {
    document.dispatchEvent(
      new CustomEvent("open-archive-modal", {
        detail: {
          context: "ingredient",
          ingredientId: props.ingredientId,
          ingredientName: props.ingredientName || "this ingredient",
          isArchived: props.archived,
        },
      })
    );
  }

  const disabledArchiveTippy = _computed(() => ({
    content: props.archiveDisabledReason,
    allowHTML: true,
    theme: "recipal",
    appendTo: () => document.body,
    // Must exceed our modal z-index (2147483640 in bulma_ext.scss).
    zIndex: 2147483647,
  }));
</script>

<template>
  <DropdownWidget>
    <template #trigger>
      <button class="button is-outlined is-info is-small">
        <span>Actions</span>
        <span class="icon is-small">
          <i class="fas fa-angle-down"></i>
        </span>
      </button>
    </template>

    <template #content>
      <a class="dropdown-item" :href="ingredientPath(ingredientId, { user_id: userId })">
        <span class="icon-text">
          <span class="icon">
            <i class="fa fa-eye"></i>
          </span>
          <span>View</span>
        </span>
      </a>

      <a class="dropdown-item" :href="editIngredientPath(ingredientId)">
        <span class="icon-text">
          <span class="icon">
            <i class="fa fa-edit"></i>
          </span>
          <span>Edit</span>
        </span>
      </a>

      <a
        class="dropdown-item"
        :href="showRecipesForIngredientPath(ingredientId, { user_id: userId })"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="fa fa-search"></i>
          </span>
          <span>Show recipes</span>
        </span>
      </a>

      <hr class="dropdown-divider" />

      <a
        class="dropdown-item"
        data-method="post"
        :href="copyIngredientPath(ingredientId, { user_id: userId })"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="fa fa-copy"></i>
          </span>
          <span>Copy</span>
        </span>
      </a>

      <hr class="dropdown-divider" />

      <a
        v-if="canArchive && !archived"
        class="dropdown-item"
        href="#"
        role="button"
        @click.prevent="openArchiveModal"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="far fa-eye-slash"></i>
          </span>
          <span>Archive</span>
        </span>
      </a>

      <span
        v-if="!canArchive && !archived"
        v-tippy="disabledArchiveTippy"
        class="dropdown-item"
        style="cursor: not-allowed; opacity: 0.5"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="far fa-eye-slash"></i>
          </span>
          <span>Archive</span>
        </span>
      </span>

      <a
        v-if="canArchive && archived"
        class="dropdown-item"
        href="#"
        role="button"
        @click.prevent="openArchiveModal"
      >
        <span class="icon-text">
          <span class="icon has-text-success">
            <i class="far fa-trash-undo"></i>
          </span>
          <span>Restore</span>
        </span>
      </a>

      <a
        class="dropdown-item"
        data-confirm="Are you sure you want to permanently delete this Ingredient?"
        data-method="delete"
        :href="ingredientPath(ingredientId)"
      >
        <span class="icon-text">
          <span class="icon has-text-danger">
            <i class="fa fa-trash-alt"></i>
          </span>
          <span>Delete</span>
        </span>
      </a>
    </template>
  </DropdownWidget>
</template>
